import React, { useState, useEffect, Component, ReactNode } from 'react';
import { auth, db, isQuotaError } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, getDocFromServer } from 'firebase/firestore';
import { handleFirestoreError, OperationType } from './firebase';
import Layout from './components/Layout';
import Login from './components/Login';
import Attendance from './components/Attendance';
import StaffAbsence from './components/StaffAbsence';
import TransportAttendance from './components/TransportAttendance';
import Reports from './components/Reports';
import ImportCSV from './components/ImportCSV';
import Classes from './components/Classes';
import Dashboard from './components/Dashboard';
import StudentManagement from './components/StudentManagement';
import StaffManagement from './components/StaffManagement';
import Settings from './components/Settings';
import TransportManagement from './components/TransportManagement';
import UserManagement from './components/UserManagement';
import StaffSelfEntry from './components/StaffSelfEntry';
import BackupManager from './components/BackupManager';
import Holidays from './components/Holidays';
import { getCachedDoc } from './services/dataCacheService';
import { Loader2, AlertTriangle } from 'lucide-react';
import { getLastBackup, shouldTriggerAutoBackup, performBackup } from './services/backupService';
import { safeStorage } from './services/safeStorage';
import { AcademicYearProvider } from './context/AcademicYearContext';

// Fallback Error Boundary to catch UI-breaking issues
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false, error: null };
  public props: ErrorBoundaryProps;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.props = props;
  }
  static getDerivedStateFromError(error: any): ErrorBoundaryState {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-10 text-center" dir="rtl">
          <div className="p-6 bg-rose-100 text-rose-600 rounded-2xl mb-6 shadow-sm">
            <AlertTriangle size={64} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">מצטערים, ארעה שגיאה בטעינת הרכיב</h2>
          <p className="text-slate-500 mb-8 max-w-md">{this.state.error?.message || 'שגיאה לא ידועה במערכת'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-10 py-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100"
          >
            טען מחדש
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  // Robust detection for self-entry mode (supports query params and hash fragments)
  const search = window.location.search;
  const hash = window.location.hash;
  const fullUrl = window.location.href;
  
  const isSelfEntry = fullUrl.includes('mode=self-entry');
  const isEducational = fullUrl.includes('type=educational');
  const isAssistant = fullUrl.includes('type=assistant');
  const entryType = isEducational ? 'educational' : (isAssistant ? 'assistant' : null);

  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [assignedClassId, setAssignedClassId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    console.log("App component mounted. Current loading state:", loading);
    const timer = setTimeout(() => {
      if (loading) {
        console.warn("Loading timeout reached (3.5s). Forcing loading completion.");
        setLoadingTimeout(true);
        setLoading(false); // Unblock the UI automatically!
      }
    }, 3500); 
    return () => clearTimeout(timer);
  }, [loading]);
  const [reportTypeToOpen, setReportTypeToOpen] = useState<string | undefined>(undefined);
  const [selectedClassIdForAttendance, setSelectedClassIdForAttendance] = useState<string | null>(null);

  useEffect(() => {
    if (userRole === 'teacher' && activeTab === 'dashboard') {
      setActiveTab('attendance');
    }
  }, [userRole, activeTab]);

  const handleSetActiveTab = (tab: string, reportType?: string, classId?: string) => {
    setActiveTab(tab);
    setReportTypeToOpen(reportType);
    if (classId) {
      setSelectedClassIdForAttendance(classId);
    } else {
      setSelectedClassIdForAttendance(null);
    }
  };

  const isAdminEmail = (email: string | null) => {
    if (!email) return false;
    const e = email.toLowerCase();
    return e === "1002380969@edu-haifa.org.il" || 
           e === "almajdb@gmail.com" || 
           e === "almajdbs@gmail.com" ||
           e === "abo.majid.st@gmail.com";
  };

  useEffect(() => {
    const handleQuotaExceeded = () => {
      setQuotaExceeded(true);
    };
    window.addEventListener('firestore-quota-exceeded', handleQuotaExceeded);
    
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      console.log("onAuthStateChanged triggered:", u ? "User detected" : "No user");
      if (u) {
        console.log("Auth state changed - User logged in:", { email: u.email, uid: u.uid });
        setUser(u);
        const initialRole = isAdminEmail(u.email) ? "admin" : "teacher";
        setUserRole(initialRole);
        setLoading(false); // Unblock immediately!

        // Background fetch maintenance mode status and profile
        (async () => {
          try {
            const settings = await getCachedDoc<any>('systemSettings', 'general', false, 10 * 60 * 1000);
            if (settings) {
              setMaintenanceMode(settings.maintenanceMode || false);
            }
          } catch (e) {
            if (isQuotaError(e)) setQuotaExceeded(true);
            console.warn("Error fetching maintenance mode:", e);
          }

          try {
            const userData = await getCachedDoc<any>('users', u.uid, false, 5 * 60 * 1000);
            if (userData) {
              const role = isAdminEmail(u.email) ? "admin" : (userData.role || 'teacher');
              console.log("User role determined (from cache):", role);
              setUserRole(role);
              setAssignedClassId(userData.classId || null);
            } else {
              const isQuotaBlocked = quotaExceeded || safeStorage.getItem('firestore_quota_exceeded') === 'true';
              const userRef = doc(db, 'users', u.uid);
              const userSnap = await getDoc(userRef);
              
              if (userSnap.exists()) {
                const data = userSnap.data();
                const role = isAdminEmail(u.email) ? "admin" : (data.role || 'teacher');
                console.log("User role determined (from Firestore):", role);
                setUserRole(role);
                setAssignedClassId(data.classId || null);
                
                const lastUpdateKey = `user_last_update_${u.uid}`;
                const lastUpdate = safeStorage.getItem(lastUpdateKey);
                const now = Date.now();
                const fourHours = 4 * 60 * 60 * 1000;

                if (!isQuotaBlocked && (!lastUpdate || now - parseInt(lastUpdate) > fourHours)) {
                  setDoc(userRef, {
                    lastLogin: serverTimestamp(),
                    displayName: u.displayName || data.displayName || '',
                    photoURL: u.photoURL || data.photoURL || '',
                    role: role 
                  }, { merge: true })
                  .then(() => safeStorage.setItem(lastUpdateKey, now.toString()))
                  .catch(err => {
                    if (isQuotaError(err)) {
                      setQuotaExceeded(true);
                      safeStorage.setItem('firestore_quota_exceeded', 'true');
                      safeStorage.setItem(lastUpdateKey, now.toString()); // Avoid retry loop
                      console.warn("User lastLogin update skipped: Quota limit reached");
                    } else {
                      console.error("Error updating last login:", err);
                    }
                  });
                }
              } else {
                console.log("Creating new user profile for:", u.email);
                const defaultRole = isAdminEmail(u.email) ? "admin" : "teacher";
                const defaultProfile = {
                  email: u.email,
                  displayName: u.displayName || '',
                  photoURL: u.photoURL || '',
                  role: defaultRole,
                  createdAt: serverTimestamp(),
                  lastLogin: serverTimestamp()
                };
                if (!isQuotaBlocked) {
                  try {
                    await setDoc(userRef, defaultProfile);
                  } catch (createErr) {
                    if (isQuotaError(createErr)) {
                      setQuotaExceeded(true);
                      safeStorage.setItem('firestore_quota_exceeded', 'true');
                      console.warn("User profile write skipped: Quota limit reached");
                    } else {
                      console.error("Error creating user profile in Firestore:", createErr);
                    }
                  }
                }
                setUserRole(defaultRole);
                setAssignedClassId(null);
              }
            }
          } catch (e) {
            if (isQuotaError(e)) {
              setQuotaExceeded(true);
              console.warn("User profile fetch paused: Quota exceeded");
              const fallbackRole = isAdminEmail(u.email) ? "admin" : "teacher";
              setUserRole(fallbackRole);
            } else {
              console.error("Error ensuring user profile:", e);
            }
          }
        })();
      } else {
        console.log("Auth state changed - No user");
        setUser(null);
        setUserRole(null);
        setAssignedClassId(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && userRole === 'admin') {
      const lastCheckKey = 'last_auto_backup_check';
      const lastCheck = safeStorage.getItem(lastCheckKey);
      const now = Date.now();
      const fourHours = 4 * 60 * 60 * 1000;

      // Throttle checking Firestore for lastBackup to at most once every 4 hours
      if (!lastCheck || now - parseInt(lastCheck) > fourHours) {
        safeStorage.setItem(lastCheckKey, now.toString());
        (async () => {
          try {
            const isQuotaBlocked = quotaExceeded || safeStorage.getItem('firestore_quota_exceeded') === 'true';
            if (isQuotaBlocked) return;

            const lastBackup = await getLastBackup();
            if (shouldTriggerAutoBackup(lastBackup)) {
              console.log("Triggering scheduled daily auto-backup for admin user...");
              const result = await performBackup(user.uid);
              if (result) {
                console.log("Daily auto-backup completed successfully:", result.id);
              }
            } else {
              console.log("Daily auto-backup skipped: last backup is within 24 hours.");
            }
          } catch (err) {
            console.warn("Auto-backup check/execution encountered an error:", err);
          }
        })();
      }
    }
  }, [userRole, user, quotaExceeded]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard setActiveTab={handleSetActiveTab} userRole={userRole} />;
      case 'attendance':
        return <Attendance userRole={userRole} assignedClassId={assignedClassId} initialClassId={selectedClassIdForAttendance} />;
      case 'staff-edu':
        return <StaffAbsence type="educational" />;
      case 'staff-assist':
        return <StaffAbsence type="assistant" />;
      case 'transport':
        return <TransportAttendance />;
      case 'reports':
        return <Reports initialReportType={reportTypeToOpen as any} userRole={userRole} assignedClassId={assignedClassId} />;
      case 'import':
        return userRole === 'admin' ? <ImportCSV /> : <Dashboard setActiveTab={handleSetActiveTab} userRole={userRole} />;
      case 'classes':
        return <Classes />;
      case 'manage-students':
        return <StudentManagement />;
      case 'manage-staff-edu':
        return <StaffManagement category="educational" />;
      case 'manage-staff-assist':
        return <StaffManagement category="assistant" />;
      case 'settings':
        return userRole === 'admin' ? <Settings /> : <Dashboard setActiveTab={handleSetActiveTab} userRole={userRole} />;
      case 'manage-carriers':
        return <TransportManagement />;
      case 'user-management':
        return userRole === 'admin' ? <UserManagement /> : <Dashboard setActiveTab={handleSetActiveTab} userRole={userRole} />;
      case 'backups':
        return userRole === 'admin' ? <BackupManager /> : <Dashboard setActiveTab={handleSetActiveTab} userRole={userRole} />;
      case 'holidays':
        return <Holidays />;
      default:
        return <Dashboard setActiveTab={handleSetActiveTab} userRole={userRole} />;
    }
  };

  return (
    <ErrorBoundary>
      <AcademicYearProvider>
        {isSelfEntry && entryType ? (
          <StaffSelfEntry type={entryType as any} />
        ) : loading ? (
          <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
            <Loader2 className="animate-spin text-indigo-600 mb-4" size={48} />
            {loadingTimeout && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 max-w-sm" dir="rtl">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center">
                  <AlertTriangle className="text-amber-500 mb-3" size={32} />
                  <h2 className="text-lg font-bold text-slate-800 mb-2">נראה שיש בעיית חיבור</h2>
                  <p className="text-slate-500 text-sm mb-6">
                    הטעינה לוקחת יותר זמן מהרגיל. ייתכן שחיבור האינטרנט איטי או שיש חסימת רשת.
                  </p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all"
                  >
                    נסה לטעון מחדש
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : !user ? (
          <Login />
        ) : maintenanceMode && userRole !== 'admin' && userRole !== 'manager' ? (
          <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center" dir="rtl">
            <div className="p-6 bg-amber-100 text-amber-600 rounded-full mb-6">
              <AlertTriangle size={64} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">המערכת בתחזוקה</h1>
            <p className="text-slate-600 max-w-md text-lg">
              המערכת נעולה כרגע לצורך עדכונים או תחזוקה שוטפת. 
              אנא נסה שוב מאוחר יותר.
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-8 px-8 py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition-all"
            >
              רענן דף
            </button>
          </div>
        ) : (
          <Layout activeTab={activeTab} setActiveTab={handleSetActiveTab} userRole={userRole}>
            {quotaExceeded && (
              <div className="bg-red-600 text-white px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-2 animate-in slide-in-from-top duration-300">
                <AlertTriangle size={18} />
                <span>מכסת השימוש בבסיס הנתונים הסתיימה להיום. ייתכן שחלק מהנתונים לא יוצגו.</span>
              </div>
            )}
            {renderContent()}
          </Layout>
        )}
      </AcademicYearProvider>
    </ErrorBoundary>
  );
}
